import { memo, SVGProps } from 'react';
import styles from './CallControls.module.less';

type ControlKind = 'mic' | 'camera' | 'screen' | 'subtitle' | 'leave';

function ControlIcon({ kind, off = false }: { kind: ControlKind; off?: boolean }) {
  const svgProps: SVGProps<SVGSVGElement> = {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.65,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };
  return (
    <svg {...svgProps}>
      {kind === 'mic' && (
        <>
          <rect x="9" y="3" width="6" height="12" rx="3" />
          <path d="M6 11v1a6 6 0 0 0 12 0v-1M12 18v3M9 21h6" />
        </>
      )}
      {kind === 'camera' && (
        <>
          <rect x="3" y="6" width="12" height="12" rx="3" />
          <path d="m15 10 5-3v10l-5-3" />
        </>
      )}
      {kind === 'screen' && (
        <>
          <rect x="3" y="4" width="18" height="13" rx="2.5" />
          <path d="M8 21h8M12 17v4m-3-11 3-3 3 3M12 7v6" />
        </>
      )}
      {kind === 'subtitle' && (
        <>
          <rect x="3" y="5" width="18" height="14" rx="4" />
          <path d="M7 10h10M7 14h6" />
        </>
      )}
      {kind === 'leave' && (
        <path d="M4 15.5v-3c0-3.3 16-3.3 16 0v3a1 1 0 0 1-1.2 1l-3.2-.7a1 1 0 0 1-.8-1v-2.1a14 14 0 0 0-5.6 0v2.1a1 1 0 0 1-.8 1l-3.2.7a1 1 0 0 1-1.2-1Z" />
      )}
      {off && (
        <>
          <path d="m4 4 16 16" strokeWidth="4.5" className={styles.slashUnderlay} />
          <path d="m4 4 16 16" />
        </>
      )}
    </svg>
  );
}

type CallControlsProps = React.HTMLAttributes<HTMLDivElement> & {
  audioOn: boolean;
  videoOn: boolean;
  screenOn: boolean;
  subtitlesOn: boolean;
  screenMode: boolean;
  mobile: boolean;
  onMic: () => void;
  onCamera: () => void;
  onScreen: () => void;
  onSubtitles: () => void;
  onLeave: () => void;
};

function CallControls({
  audioOn,
  videoOn,
  screenOn,
  subtitlesOn,
  screenMode,
  mobile,
  onMic,
  onCamera,
  onScreen,
  onSubtitles,
  onLeave,
  className = '',
  ...rest
}: CallControlsProps) {
  const screenInsteadOfCamera = screenMode && !mobile;
  const videoActive = screenInsteadOfCamera ? screenOn : videoOn;
  const videoLabel = screenInsteadOfCamera
    ? screenOn
      ? '停止屏幕共享'
      : '共享屏幕'
    : videoOn
    ? '关闭摄像头'
    : '打开摄像头';

  return (
    <div {...rest} className={`${styles.controls} ${className}`} role="group" aria-label="通话控制">
      <button
        type="button"
        className={`${styles.control} ${styles.device} ${audioOn ? '' : styles.muted}`}
        onClick={onMic}
        aria-label={audioOn ? '关闭麦克风' : '打开麦克风'}
        title={audioOn ? '关闭麦克风' : '打开麦克风'}
        aria-pressed={audioOn}
      >
        <ControlIcon kind="mic" off={!audioOn} />
      </button>
      <button
        type="button"
        className={`${styles.control} ${styles.device} ${videoActive ? '' : styles.muted}`}
        onClick={screenInsteadOfCamera ? onScreen : onCamera}
        aria-label={videoLabel}
        title={videoLabel}
        aria-pressed={videoActive}
      >
        <ControlIcon kind={screenInsteadOfCamera ? 'screen' : 'camera'} off={!videoActive} />
      </button>
      {screenMode && mobile && (
        <button
          type="button"
          className={`${styles.control} ${screenOn ? styles.active : styles.muted}`}
          onClick={onScreen}
          aria-label={screenOn ? '停止屏幕共享' : '共享屏幕'}
          title={screenOn ? '停止屏幕共享' : '共享屏幕'}
          aria-pressed={screenOn}
        >
          <ControlIcon kind="screen" off={!screenOn} />
        </button>
      )}
      <button
        type="button"
        className={`${styles.control} ${subtitlesOn ? styles.active : styles.muted}`}
        onClick={onSubtitles}
        aria-label={subtitlesOn ? '关闭字幕' : '打开字幕'}
        title={subtitlesOn ? '关闭字幕' : '打开字幕'}
        aria-pressed={subtitlesOn}
      >
        <ControlIcon kind="subtitle" off={!subtitlesOn} />
      </button>
      <span className={styles.divider} aria-hidden="true" />
      <button
        type="button"
        className={`${styles.control} ${styles.leave}`}
        onClick={onLeave}
        aria-label="结束通话"
        title="结束通话"
      >
        <ControlIcon kind="leave" />
      </button>
    </div>
  );
}

export default memo(CallControls);
